import { instance } from "api/apiBase";
import { useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import { AccountStatusVariant } from "../constants";
import { getErrorMessage } from "utils/error";

import { Verification } from "../types";
import { toast } from "react-toastify";
import { useAuthContext } from "providers/Auth";

const query = "portal/v1/customers/all";

export const useRentReportingController = () => {
  const [sorted, setSorted] = useState<{ key: string; desc: boolean }>({
    key: "",
    desc: true
  });

  const { type } = useAuthContext();

  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      search: "",
      per_page: "10",
      page: "1",
      type: AccountStatusVariant.NeedToReport
    },
    ["per_page", "page", "search", "type"]
  );

  const [debouncedParams] = useDebouncedValue(searchParams.search, 500);

  const getSubscribers = async (type?: string) => {
    return instance
      .get(query, {
        params: {
          per_page: searchParams.per_page || "10",
          page: searchParams.page || "1",
          search: debouncedParams || "",
          type: type
            ? type
            : searchParams.type === AccountStatusVariant.All
              ? undefined
              : searchParams.type,
          order_direction: sorted.desc ? "desc" : "asc",
          order_by: sorted.key || "id"
        }
      })
      .then(({ data }) => data);
  };

  const subscribersMeta = useQuery({
    queryKey: [
      query,
      {
        per_page: searchParams.per_page || "10",
        page: searchParams.page || "1",
        search: debouncedParams || "",
        type: searchParams.type || undefined,
        order_direction: sorted.desc ? "desc" : "asc",
        order_by: sorted.key || "id"
      }
    ],
    queryFn: () => getSubscribers(),
    placeholderData: keepPreviousData,
    enabled: type.isRentReporting || type.isSDK
  });

  const refetchAfterReport = () => {
    queryClient.invalidateQueries({
      queryKey: [query]
    });
    queryClient.invalidateQueries({
      queryKey: [
        query + "__total",
        { page: 1, per_page: 1, type: AccountStatusVariant.NeedToReport }
      ]
    });
  };

  const { mutate: reportSingleRenter, isPending: isReportingSingleRenter } =
    useMutation({
      mutationKey: ["REPORT_SINGLE_RENTER"],
      mutationFn: (data: {
        subscriberId: number;
        verificationId: number;
        verifications: Verification;
      }) => {
        return instance.patch(
          `/portal/v1/customers/${data.subscriberId}/verifications/${data.verificationId}`,
          { ...data.verifications }
        );
      },
      onSuccess: () => {
        toast.success("Renter has been successfully reported");
        refetchAfterReport();
        queryClient.invalidateQueries({ queryKey: ["SUBSCRIBER_DETAIL"] });
      },
      onError: (error) => {
        toast.error(getErrorMessage(error));
      }
    });

  const { mutate: reportAllRenters, isPending: isReportingAllRenters } =
    useMutation({
      mutationKey: ["REPORT_ALL_RENTERS"],
      mutationFn: (data: {
        verifications: Verification[];
        defaultAction: string;
      }) => {
        return instance.patch(`/portal/v1/customers`, {
          verifications: data.verifications,
          default_action: data.defaultAction
        });
      },
      onSuccess: () => {
        toast.success("Renters have been successfully reported");
        refetchAfterReport();
        setSearchParams({ ...searchParams, page: "1" });
      },
      onError: (error) => {
        toast.error(getErrorMessage(error));
      }
    });

  return {
    subscribersMeta,
    reportSingleRenter,
    searchParams,
    setSearchParams,
    debouncedParams,
    reportAllRenters,
    isReportingSingleRenter,
    isReportingAllRenters,
    sorted,
    setSorted,
    getSubscribers
  };
};
